import { message as AntMessage } from 'antd';
import type { AxiosRequestConfig, AxiosResponse, Method } from 'axios';
import axios from 'axios';
import { omit } from 'lodash';
import { getToken } from './userInfo';

type ApiFunction = (
    url: string,
    data?: any,
    options?: Omit<AxiosRequestConfig, 'data' | 'url' | 'method'> & {
        handleErr?: (params: unknown) => void;
        formDataMethod?: 'application/x-www-form-urlencoded' | 'multipart/form-data';
    },
) => Promise<any | void>;

const handleResponse = (response: AxiosResponse<any, any>) => {
    if (response?.data?.isSuccess) {
        if (response.config.url?.match(/logout/g)) {
            window.history.pushState(null, '', '/portal/login');
            window.localStorage.clear();
            window.sessionStorage.clear();
            return;
        }

        return response.data?.data;
    }

    return Promise.reject(response.data);
};

const instance = axios.create({
    headers: {
        'X-Requested-With': 'XMLHttpRequest',
    },
});

const methods: Method[] = ['get', 'post', 'put', 'delete'];

export default methods.reduce((apiObject, key) => {
    const apiFun: ApiFunction = (url, data, options): Promise<any | void> | any | void => {
        if (options?.formDataMethod) {
            options.headers = { ...options.headers, 'content-type': options.formDataMethod };

            if (options.formDataMethod === 'application/x-www-form-urlencoded') {
                data = new URLSearchParams(data);
            }
        }

        if (['get'].includes(key)) {
            data = { params: data };
        } else {
            data = { data };
        }

        return instance
            .request({
                url,
                method: key,
                baseURL: '/api',
                ...data,
                ...omit(options, ['handleErr', 'headers']),
                headers: {
                    token: getToken() as string,
                    ...(options?.headers ?? {}),
                },
            })
            .then(response => {
                if (Object.prototype.toString.call(response.data).slice(8, -1) === 'Blob') {
                    if (response.data.type === 'application/json') {
                        return (response.data as Blob)
                            .text()
                            .then(text => {
                                const data = JSON.parse(text);

                                response.data = data;

                                return handleResponse(response);
                            })
                            .catch(error => {
                                return Promise.reject(error);
                            });
                    }

                    return response;
                }

                return handleResponse(response);
            })
            .catch((err: any) => {
                // eslint-disable-next-line no-console
                console.error(err);

                const message: string =
                    err.resultMessage ||
                    err.response?.data?.message ||
                    err.message ||
                    '请求失败，请联系管理员处理';

                if (typeof options?.handleErr === 'function') {
                    options.handleErr(err);
                    return;
                }

                AntMessage.error(message);

                if (err?.resultCode === '300' && !err?.isSuccess) {
                    window.history.pushState(null, '', '/portal/login');
                    window.localStorage.clear();
                    window.sessionStorage.clear();
                } else {
                    throw new Error(message);
                }
            });
    };
    apiObject[key] = apiFun;

    return apiObject;
}, {} as Record<Method, ApiFunction>);
